import type { ShellType } from '../store/terminalStore';

const DB_NAME = 'terra-terminal';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';

type TerminalSource = 'ai' | 'user';

export type SessionMapping = {
  sessionId: string;
  terminalId: string;
  chatId?: string;
  toolCallId?: string;
  isBackground?: boolean;
  shell?: ShellType;
  createdAt: number;
  source: TerminalSource;
};

export type EnsureTerminalResult = {
  terminalId: string;
  shell: ShellType;
  isNewSession: boolean;
};

export type EnsureTerminalSessionOptions = {
  sessionId: string;
  cwd?: string;
  shell?: ShellType;
  scopeKey?: string;
  defaultShell?: ShellType;
  cols?: number;
  rows?: number;
  source?: TerminalSource;
  chatId?: string;
  toolCallId?: string;
  isBackground?: boolean;
};

let dbInstance: IDBDatabase | null = null;

async function openDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    try {
      const tx = dbInstance.transaction(STORE_NAME, 'readonly');

      tx.abort();

      return dbInstance;
    } catch (error) {
      console.warn(
        '[TerminalExecution] Cached DB connection stale, reopening',
        error,
      );
      dbInstance = null;
    }
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const db = request.result;

      db.onclose = () => {
        if (dbInstance === db) {
          dbInstance = null;
        }
      };

      dbInstance = db;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'sessionId' });
      }
    };
  });
}

async function loadSessionMappings(): Promise<Map<string, SessionMapping>> {
  try {
    const db = await openDB();

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const mappings = new Map<string, SessionMapping>();

        for (const item of request.result as SessionMapping[]) {
          mappings.set(item.sessionId, item);
        }

        resolve(mappings);
      };

      request.onerror = () => {
        console.warn(
          '[TerminalExecution] IndexedDB getAll failed',
          request.error,
        );
        resolve(new Map());
      };
    });
  } catch (error) {
    console.warn(
      '[TerminalExecution] Failed to load session mappings from IndexedDB',
      error,
    );

    return new Map();
  }
}

async function saveSessionMapping(mapping: SessionMapping): Promise<void> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(mapping);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('[TerminalExecution] Failed to save session mapping', error);
  }
}

async function deleteSessionMapping(sessionId: string): Promise<void> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(sessionId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('[TerminalExecution] Failed to delete session mapping', error);
  }
}

const sessionTerminals = new Map<string, SessionMapping>();
let initPromise: Promise<void> | null = null;

const sessionLocks = new Map<string, Promise<unknown>>();

async function withSessionLock<T>(
  sessionId: string,
  fn: () => Promise<T>,
): Promise<T> {
  const prev = sessionLocks.get(sessionId) ?? Promise.resolve();

  const execute = async () => {
    try {
      await prev;
    } catch (error) {
      console.warn(
        '[TerminalExecution] Previous session lock failed, proceeding',
        error,
      );
    }

    return fn();
  };

  const result = execute();
  const tail = result.then(
    () => undefined,
    () => undefined,
  );

  sessionLocks.set(sessionId, tail);

  try {
    return await result;
  } finally {
    if (sessionLocks.get(sessionId) === tail) {
      sessionLocks.delete(sessionId);
    }
  }
}

export async function ensureInitialized(): Promise<void> {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      const mappings = await loadSessionMappings();

      for (const [key, value] of mappings) {
        if (!sessionTerminals.has(key)) {
          sessionTerminals.set(key, value);
        }
      }
    } catch (error) {
      console.warn(
        '[TerminalExecution] Failed to load session mappings',
        error,
      );
      initPromise = null;
    }
  })();

  return initPromise;
}

function applySessionMappingMeta({
  mapping,
  chatId,
  toolCallId,
  isBackground,
}: {
  mapping: SessionMapping;
  chatId?: string;
  toolCallId?: string;
  isBackground?: boolean;
}): void {
  if (chatId !== undefined) {
    mapping.chatId = chatId;
  }

  if (toolCallId !== undefined) {
    mapping.toolCallId = toolCallId;
  }

  if (isBackground !== undefined) {
    mapping.isBackground = isBackground;
  }
}

async function stampExistingSession({
  existing,
  sessionId,
  cwd,
  scopeKey,
  cols,
  rows,
  chatId,
  toolCallId,
  isBackground,
  defaultShell,
}: {
  existing: SessionMapping;
  sessionId: string;
  cwd?: string;
  scopeKey?: string;
  cols?: number;
  rows?: number;
  chatId?: string;
  toolCallId?: string;
  isBackground?: boolean;
  defaultShell?: ShellType;
}): Promise<EnsureTerminalResult> {
  await window.electronAPI.terminal.create({
    sessionId,
    cwd,
    shell: existing.shell,
    scopeKey,
    cols,
    rows,
    chatId,
    toolCallId,
    isBackground,
  });

  applySessionMappingMeta({
    mapping: existing,
    chatId,
    toolCallId,
    isBackground,
  });
  await saveSessionMapping(existing);

  return {
    terminalId: existing.terminalId,
    shell: existing.shell || defaultShell || 'zsh',
    isNewSession: false,
  };
}

export function getSessionMapping(
  sessionId: string,
): SessionMapping | undefined {
  return sessionTerminals.get(sessionId);
}

export async function ensureSessionTerminal({
  sessionId,
  cwd,
  shell,
  scopeKey,
  defaultShell,
  cols,
  rows,
  source = 'user',
  chatId,
  toolCallId,
  isBackground,
}: EnsureTerminalSessionOptions): Promise<EnsureTerminalResult> {
  await ensureInitialized();

  return withSessionLock(sessionId, async () => {
    if (typeof window === 'undefined' || !window.electronAPI?.terminal) {
      throw new Error('Terminal API is unavailable');
    }

    const existing = sessionTerminals.get(sessionId);

    if (existing) {
      const exists = await window.electronAPI.terminal.exists(
        existing.terminalId,
      );

      if (exists) {
        return stampExistingSession({
          existing,
          sessionId,
          cwd,
          scopeKey,
          cols,
          rows,
          chatId,
          toolCallId,
          isBackground,
          defaultShell,
        });
      }

      sessionTerminals.delete(sessionId);
      await deleteSessionMapping(sessionId);
    }

    const shellForSession = shell ?? existing?.shell;
    const createdTerminal = await window.electronAPI.terminal.create({
      sessionId,
      cwd,
      shell: shellForSession,
      scopeKey,
      cols,
      rows,
      chatId,
      toolCallId,
      isBackground,
    });

    const mappingShell = shellForSession || defaultShell || 'zsh';
    const mapping: SessionMapping = {
      sessionId,
      terminalId: createdTerminal.terminalId,
      chatId: chatId ?? sessionId,
      toolCallId,
      isBackground,
      shell: mappingShell,
      createdAt: Date.now(),
      source,
    };

    sessionTerminals.set(sessionId, mapping);
    await saveSessionMapping(mapping);

    return {
      terminalId: createdTerminal.terminalId,
      shell: mappingShell,
      isNewSession: true,
    };
  });
}

export async function killSessionTerminal(sessionId: string): Promise<void> {
  const mapping = sessionTerminals.get(sessionId);

  if (
    mapping &&
    typeof window !== 'undefined' &&
    window.electronAPI?.terminal
  ) {
    try {
      await window.electronAPI.terminal.kill(mapping.terminalId);
    } catch (error) {
      console.warn('[TerminalExecution] Failed to kill terminal', error);
    }

    sessionTerminals.delete(sessionId);
    await deleteSessionMapping(sessionId);
  }
}
